import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useSessionSnapshot } from "../auth/session";
import { useApi } from "./context";
import { isApiError } from "./errors";
import { local, withOffline } from "../offline/catalogueCache";

/** Query keys in one place, so mutations invalidate precisely what they change. */
export const qk = {
  me: ["me"] as const,
  branding: ["branding"] as const,
  branch: (id: number | null) => ["branch", id] as const,
  effectivePermissions: ["permissions", "effective"] as const,
  branchPermissions: ["permissions", "branch"] as const,
  tillCurrent: ["till", "current"] as const,
  tillLastClosed: ["till", "last-closed"] as const,
  plans: ["plans"] as const,
  categories: ["categories"] as const,
  favorites: ["products", "favorites"] as const,
  byCategory: (id: number) => ["products", "by-category", id] as const,
  productSearch: (q: string) => ["products", "search", q] as const,
  products: ["products"] as const,
  batches: (productId: number) => ["batches", productId] as const,
  customers: ["customers"] as const,
  customerSearch: (q: string) => ["customers", "search", q] as const,
  dispensers: ["fuel", "dispensers"] as const,
  tanks: ["fuel", "tanks"] as const,
  inventory: ["inventory"] as const,
};

/** The till's current session, last close and history. Refreshed after any till change, and
 * after a 409, which means another terminal opened or closed the till meanwhile. */
export function refreshTill(qc: QueryClient): void {
  for (const k of [qk.tillCurrent, qk.tillLastClosed, ["till", "history"]]) void qc.invalidateQueries({ queryKey: k });
}
export const isConflict = (e: unknown): boolean => isApiError(e) && e.status === 409;

/** Everything that shows stock or product lists: products (checkout, back office), the
 * inventory table and selects, and the reports (low stock, top products). Any change to a
 * product or its stock refreshes all of them. */
export function invalidateStock(qc: QueryClient): void {
  for (const k of [qk.products, qk.inventory, ["reports"]]) void qc.invalidateQueries({ queryKey: k });
}

function useTenantSession() {
  const s = useSessionSnapshot();
  const active = s.impersonation ?? s.session;
  return active && active.user.role !== "SUPERADMIN" ? active : null;
}

export function useBranding() {
  const { api } = useApi();
  const s = useTenantSession();
  return useQuery({ queryKey: qk.branding, queryFn: api.branding.get, enabled: !!s, staleTime: 5 * 60_000 });
}

export function useCurrentBranch() {
  const { api } = useApi();
  const s = useTenantSession();
  const id = s?.user.branchId ?? null;
  return useQuery({ queryKey: qk.branch(id), queryFn: () => api.branches.get(id!), enabled: id !== null, staleTime: 5 * 60_000 });
}

export function useEffectivePermissions() {
  const { api } = useApi();
  const s = useTenantSession();
  return useQuery({ queryKey: qk.effectivePermissions, queryFn: api.permissions.effective, enabled: !!s, staleTime: 60_000 });
}

/** The open till session for this branch, or null (204). Drives the top-bar pill. */
export function useTillCurrent() {
  const { api } = useApi();
  const s = useTenantSession();
  return useQuery({ queryKey: qk.tillCurrent, queryFn: api.till.current, enabled: !!s, staleTime: 15_000, refetchInterval: 60_000 });
}

export function useCategories() {
  const { api } = useApi();
  const s = useTenantSession();
  const tenantId = s?.user.tenantId ?? null;
  return useQuery({ queryKey: qk.categories, queryFn: () => withOffline(tenantId, api.categories.list, local.categories), enabled: !!s, staleTime: 5 * 60_000 });
}

export function useDispensers(enabled = true) {
  const { api } = useApi();
  const s = useTenantSession();
  return useQuery({ queryKey: qk.dispensers, queryFn: api.fuel.dispensers, enabled: !!s && enabled, staleTime: 5 * 60_000 });
}
